import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { ApiClientError } from '@/lib/api/client'
import { updateCampaignSaleStatus } from '@/lib/campaign-reports'
import {
  buildStatusTransitionOptions,
  formatStatusLabel,
  MAX_STATUS_CHANGE_REASON_LENGTH,
  MIN_STATUS_CHANGE_REASON_LENGTH,
  normalizePaymentStatus,
  resolveStatusTransitions,
  statusBadgeVariant,
  statusChangeConsequenceHint,
  type CampaignPaymentListStatus,
} from '@/lib/payment-status'
import type {
  CampaignSaleDetailPayload,
  CampaignSaleStatusChangeEntry,
  PaymentConfirmationSource,
} from '@/types/campaign-report'
import { PedidoStatusHistorySection } from './PedidoStatusHistorySection'
import styles from './PedidoStatusControl.module.css'

export interface PedidoStatusControlProps {
  campaignId: number
  paymentId: number
  currentStatus: string
  allowedTransitions?: CampaignPaymentListStatus[]
  confirmationSource?: PaymentConfirmationSource | null
  isManualApproval?: boolean
  statusChanges?: CampaignSaleStatusChangeEntry[]
  canChangeStatus?: boolean
  token?: string | null
  disabled?: boolean
  showHistoryInMenu?: boolean
  onStatusChanged?: (detail: CampaignSaleDetailPayload) => void
  onListItemChanged?: (patch: {
    status: CampaignPaymentListStatus
    confirmation_source?: PaymentConfirmationSource | null
    is_manual_approval?: boolean
    allowed_status_transitions?: CampaignPaymentListStatus[]
    status_changes?: CampaignSaleStatusChangeEntry[]
    paid_at?: string | null
  }) => void
}

function badgeClassName(status: string): string {
  const variant = statusBadgeVariant(status)
  switch (variant) {
    case 'paid':
      return styles.statusPaid
    case 'pending':
      return styles.statusPending
    case 'expired':
      return styles.statusExpired
    case 'canceled':
      return styles.statusCanceled
    case 'failed':
      return styles.statusFailed
    default:
      return styles.statusUnknown
  }
}

function statusTriggerClassName(status: string, interactive: boolean): string {
  return [
    badgeClassName(status),
    styles.statusTrigger,
    interactive ? styles.statusTriggerInteractive : styles.statusTriggerReadOnly,
  ].join(' ')
}

export function PedidoStatusControl({
  campaignId,
  paymentId,
  currentStatus,
  allowedTransitions = [],
  confirmationSource = null,
  isManualApproval = false,
  statusChanges,
  canChangeStatus = false,
  token,
  disabled = false,
  showHistoryInMenu = true,
  onStatusChanged,
  onListItemChanged,
}: PedidoStatusControlProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<CampaignPaymentListStatus | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuPanelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 168 })
  const [reason, setReason] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [localStatusChanges, setLocalStatusChanges] = useState<
    CampaignSaleStatusChangeEntry[]
  >(statusChanges ?? [])
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  useEffect(() => {
    setLocalStatusChanges(statusChanges ?? [])
  }, [statusChanges])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
  }, [])

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return

    const rect = triggerRef.current.getBoundingClientRect()
    setMenuPosition({
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(rect.width, 168),
    })
  }, [])

  useEffect(() => {
    if (!menuOpen) return

    updateMenuPosition()

    const handleReposition = () => updateMenuPosition()
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [menuOpen, updateMenuPosition])

  useEffect(() => {
    if (!menuOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target)) return
      if (menuPanelRef.current?.contains(target)) return
      closeMenu()
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeMenu, menuOpen])

  useEffect(() => {
    if (!historyOpen && pendingStatus == null && !menuOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (historyOpen) {
        setHistoryOpen(false)
        return
      }
      if (menuOpen) {
        closeMenu()
        return
      }
      if (pendingStatus != null && !submitting) {
        setPendingStatus(null)
        setReason('')
        setCurrentPassword('')
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [closeMenu, historyOpen, menuOpen, pendingStatus, submitting])

  const normalizedStatus = normalizePaymentStatus(currentStatus)

  const effectiveTransitions = useMemo(
    () =>
      canChangeStatus
        ? resolveStatusTransitions(currentStatus, allowedTransitions)
        : [],
    [allowedTransitions, canChangeStatus, currentStatus]
  )

  const transitionOptions = useMemo(
    () => buildStatusTransitionOptions(effectiveTransitions),
    [effectiveTransitions]
  )

  const consequenceHint = useMemo(
    () => statusChangeConsequenceHint(currentStatus, pendingStatus),
    [currentStatus, pendingStatus]
  )

  const canChangeStatusOptions =
    canChangeStatus && !disabled && transitionOptions.length > 0

  const canOpenMenu =
    !disabled && (canChangeStatusOptions || showHistoryInMenu)

  const historyCount = localStatusChanges.length

  const handleOpenHistory = () => {
    closeMenu()
    setHistoryOpen(true)
  }

  const handleStatusPick = (value: string) => {
    const next = normalizePaymentStatus(value)
    if (!next) return

    closeMenu()
    setPendingStatus(next)
    setReason('')
    setCurrentPassword('')
    setFeedback(null)
  }

  const statusMenu = menuOpen && canOpenMenu ? (
    <div
      ref={menuPanelRef}
      id={`pedido-status-menu-${paymentId}`}
      role="menu"
      aria-label="Opções do status do pedido"
      className={styles.statusMenu}
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        minWidth: `${menuPosition.width}px`,
      }}
    >
      {canChangeStatusOptions ? (
        <ul
          role="group"
          aria-label="Alterar status"
          className={styles.statusMenuList}
        >
          {transitionOptions.map((option) => (
            <li key={option.value} role="none">
              <button
                type="button"
                role="menuitem"
                className={[
                  statusTriggerClassName(option.value, true),
                  styles.statusMenuOption,
                ].join(' ')}
                onClick={() => handleStatusPick(option.value)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {canChangeStatusOptions && showHistoryInMenu ? (
        <div className={styles.statusMenuDivider} role="separator" />
      ) : null}

      {showHistoryInMenu ? (
        <button
          type="button"
          role="menuitem"
          className={styles.statusMenuHistory}
          onClick={handleOpenHistory}
        >
          <Icon icon="mdi:history" className={styles.statusMenuHistoryIcon} aria-hidden />
          <span>
            Histórico
            {historyCount > 0 ? ` (${historyCount})` : ''}
          </span>
        </button>
      ) : null}
    </div>
  ) : null

  const handleCloseModal = () => {
    if (submitting) return
    setPendingStatus(null)
    setReason('')
    setCurrentPassword('')
  }

  const handleConfirm = async () => {
    if (pendingStatus == null) return

    const trimmedReason = reason.trim()
    if (trimmedReason.length < MIN_STATUS_CHANGE_REASON_LENGTH) {
      setFeedback({
        type: 'error',
        message: `Informe um motivo com pelo menos ${MIN_STATUS_CHANGE_REASON_LENGTH} caracteres.`,
      })
      return
    }

    if (currentPassword.trim() === '') {
      setFeedback({
        type: 'error',
        message: 'Informe sua senha para confirmar a alteração.',
      })
      return
    }

    setSubmitting(true)
    setFeedback(null)

    try {
      const detail = await updateCampaignSaleStatus(
        campaignId,
        paymentId,
        {
          status: pendingStatus,
          reason: trimmedReason,
          current_password: currentPassword,
        },
        token
      )

      const nextChanges = detail.status_changes ?? []
      setLocalStatusChanges(nextChanges)

      onStatusChanged?.(detail)
      onListItemChanged?.({
        status: normalizePaymentStatus(detail.payment.status) ?? pendingStatus,
        confirmation_source: detail.confirmation?.source ?? null,
        is_manual_approval: detail.confirmation?.source === 'manual',
        allowed_status_transitions: detail.allowed_status_transitions,
        status_changes: nextChanges,
        paid_at: detail.payment.paid_at,
      })

      setPendingStatus(null)
      setReason('')
      setCurrentPassword('')
      setFeedback({
        type: 'success',
        message: 'Status atualizado com sucesso.',
      })
      window.setTimeout(() => setFeedback(null), 2500)
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Não foi possível alterar o status. Tente novamente.'
      setFeedback({ type: 'error', message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className={styles.statusCell}>
        <div className={styles.statusMenuWrap} ref={menuRef}>
          {canOpenMenu ? (
            <button
              ref={triggerRef}
              id={`pedido-status-${paymentId}`}
              type="button"
              className={statusTriggerClassName(currentStatus, true)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-controls={`pedido-status-menu-${paymentId}`}
              aria-label={
                canChangeStatusOptions
                  ? `Status do pedido: ${formatStatusLabel(currentStatus)}. Clique para alterar ou ver histórico.`
                  : `Status do pedido: ${formatStatusLabel(currentStatus)}. Clique para ver histórico.`
              }
              disabled={submitting}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span>{formatStatusLabel(currentStatus)}</span>
              <Icon
                icon="mdi:chevron-down"
                className={[styles.statusChevron, menuOpen && styles.statusChevronOpen]
                  .filter(Boolean)
                  .join(' ')}
                aria-hidden
              />
            </button>
          ) : (
            <span
              className={statusTriggerClassName(currentStatus, false)}
              aria-label={`Status do pedido: ${formatStatusLabel(currentStatus)}`}
            >
              {formatStatusLabel(currentStatus)}
            </span>
          )}
        </div>

        {isManualApproval ||
        (confirmationSource === 'doey_webhook' && normalizedStatus === 'paid') ? (
          <div className={styles.statusRow}>
            {isManualApproval ? (
              <span className={styles.confirmationChipManual}>Manual</span>
            ) : null}
            {confirmationSource === 'doey_webhook' &&
            normalizedStatus === 'paid' ? (
              <span className={styles.confirmationChipPix}>PIX</span>
            ) : null}
          </div>
        ) : null}

        {feedback && !pendingStatus ? (
          <p
            className={
              feedback.type === 'success'
                ? styles.feedbackSuccess
                : styles.feedbackError
            }
            role="status"
          >
            {feedback.message}
          </p>
        ) : null}
      </div>

      {typeof document !== 'undefined' && statusMenu
        ? createPortal(statusMenu, document.body)
        : null}

      {historyOpen ? (
        <div
          className={styles.modalOverlay}
          role="presentation"
          onClick={() => setHistoryOpen(false)}
        >
          <div
            className={styles.historyModalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`status-history-title-${paymentId}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.historyModalHeader}>
              <h2
                id={`status-history-title-${paymentId}`}
                className={styles.modalTitle}
              >
                Histórico de status
              </h2>
              <button
                type="button"
                className={styles.historyCloseBtn}
                onClick={() => setHistoryOpen(false)}
                aria-label="Fechar histórico"
              >
                Fechar
              </button>
            </div>
            <div className={styles.historyModalBody}>
              <PedidoStatusHistorySection
                entries={localStatusChanges}
                showTitle={false}
              />
            </div>
          </div>
        </div>
      ) : null}

      {pendingStatus ? (
        <div
          className={styles.modalOverlay}
          role="presentation"
          onClick={handleCloseModal}
        >
          <div
            className={styles.modalCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`status-change-title-${paymentId}`}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id={`status-change-title-${paymentId}`} className={styles.modalTitle}>
              Alterar status para {formatStatusLabel(pendingStatus)}
            </h2>
            <p className={styles.modalHint}>
              Informe o motivo e confirme com sua senha. Esta ação ficará registrada na
              auditoria do pedido.
            </p>
            {consequenceHint ? (
              <p className={styles.consequenceHint} role="note">
                {consequenceHint}
              </p>
            ) : null}
            <label className={styles.modalLabel} htmlFor={`status-change-reason-${paymentId}`}>
              Motivo
            </label>
            <textarea
              id={`status-change-reason-${paymentId}`}
              className={styles.modalTextarea}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              maxLength={MAX_STATUS_CHANGE_REASON_LENGTH}
              rows={4}
              placeholder="Ex.: Cliente enviou comprovante por WhatsApp."
              disabled={submitting}
            />
            <p className={styles.modalCounter}>
              {reason.trim().length}/{MAX_STATUS_CHANGE_REASON_LENGTH} (mínimo{' '}
              {MIN_STATUS_CHANGE_REASON_LENGTH})
            </p>
            <label
              className={styles.modalLabel}
              htmlFor={`status-change-password-${paymentId}`}
            >
              Senha
            </label>
            <input
              id={`status-change-password-${paymentId}`}
              type="password"
              className={styles.modalPassword}
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="Digite sua senha"
              disabled={submitting}
            />
            {feedback?.type === 'error' ? (
              <p className={styles.feedbackError} role="alert">
                {feedback.message}
              </p>
            ) : null}
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.modalCancelBtn}
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={styles.modalConfirmBtn}
                onClick={() => void handleConfirm()}
                disabled={submitting}
              >
                {submitting ? 'Salvando…' : 'Confirmar alteração'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
